import { useMemo } from 'react';
import { POS_GOLD } from '../../../../../config/posRoomStatus';
import { formatServices } from './helpers';
import type { ServiceItem } from './helpers';

interface FilterSelectOption {
  value: string;
  label: string;
  matchValues?: string[];
}

interface BookingTableRow {
  customer_name: string;
  source: string;
  booking_reference: string;
  service_date: string;
  start_time: string;
  end_time: string;
  services: ServiceItem[];
  territory_name: string;
  status: string;
  operator_names: string[];
  assigned_to: string[];
  is_pos: boolean;
  [key: string]: unknown;
}

interface UseBookingsTableConfigProps {
  formatBookingDate: (dateStr: string) => string;
  statusOptions: FilterSelectOption[];
  operatorOptions: FilterSelectOption[];
  territoryOptions: FilterSelectOption[];
  contextOptions: FilterSelectOption[];
  sourceOptions: FilterSelectOption[];
}

export function useBookingsTableConfig({
  formatBookingDate, statusOptions, operatorOptions, territoryOptions, contextOptions, sourceOptions,
}: UseBookingsTableConfigProps) {

  const columns = useMemo(() => [
    {
      key: 'customer_name',
      label: 'Customer',
      sortable: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        // Match the calendar card design: small brand-coloured swatch leads
        // the cell (gold = POS, violet = CMB), then the name. Status colour
        // stays untouched on the row's status pill.
        const brandColour = row.is_pos ? POS_GOLD : 'var(--color-primary)';
        return (
          <div className="flex items-center gap-sm">
            <span style={{ display: 'inline-block', width: '4px', alignSelf: 'stretch', minHeight: '18px', borderRadius: 'var(--radius-sm)', background: brandColour, flexShrink: 0 }} aria-hidden="true" />
            <span className="fw-500 cell-truncate cell-truncate--150" title={row.customer_name}>
              {row.customer_name || '-'}
            </span>
            {row.is_pos && (
              <span style={{ background: POS_GOLD, color: 'var(--color-white)', fontSize: 'var(--text-2xs)', fontWeight: 700, padding: '1px 6px', borderRadius: '999px', letterSpacing: '0.025em' }} title="Peace of Sleep">
                POS
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: 'booking_reference',
      label: 'Reference',
      sortable: true,
      hideOnMobile: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const isSimplyBook = row.source === 'simplybook';
        return (
          <div className="cell-ref">
            {isSimplyBook && (
              <span title="SimplyBook (Legacy)" className="sb-badge">SB</span>
            )}
            <span className="text-mono-primary">{row.booking_reference}</span>
          </div>
        );
      }
    },
    {
      key: 'applied_code',
      label: 'Code',
      sortable: true,
      hideOnMobile: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const code = row.applied_code as string | undefined;
        if (!code) return <span className="text-muted">-</span>;
        const type = row.applied_code_type as string | undefined;
        return <span className="text-mono-primary" title={type ? `${type} code` : 'Code'}>{code}</span>;
      }
    },
    {
      key: 'service_date',
      label: 'Date',
      sortable: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        return (
          <div>
            <div>{formatBookingDate(row.service_date)}</div>
            <div className="text-time-sub">{row.start_time} - {row.end_time}</div>
          </div>
        );
      }
    },
    {
      key: 'services',
      label: 'Services',
      hideOnMobile: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const display = formatServices(row.services);
        return (
          <div className="cell-truncate cell-truncate--180" title={display}>
            {display}
          </div>
        );
      }
    },
    {
      key: 'territory_name',
      label: 'Territory',
      sortable: true,
      hideOnMobile: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const displayName = (row.territory_name || 'Unknown')
          .replace('CleanMyBed ', '')
          .replace(' - ', ' ')
          .trim();
        return (
          <span title={row.territory_name}>{displayName}</span>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const pillMap: Record<string, string> = {
          pending: 'pending', confirmed: 'confirmed', completed: 'completed',
          cancelled: 'cancelled', rescheduled: 'draft',
          active: 'confirmed', approved: 'confirmed', canceled: 'cancelled'
        };
        const labelMap: Record<string, string> = {
          pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed',
          cancelled: 'Cancelled', rescheduled: 'Rescheduled',
          active: 'Confirmed', approved: 'Confirmed', canceled: 'Cancelled'
        };
        const s = row.status;
        return (
          <span className={`status-pill status-pill--${pillMap[s] || 'draft'}`}>
            <span className="status-pill-dot" style={{ background: 'currentColor' }} />
            {labelMap[s] || s}
          </span>
        );
      }
    },
    {
      key: 'assigned_to',
      label: 'Assigned',
      hideOnMobile: true,
      render: (r: Record<string, unknown>) => {
        const row = r as BookingTableRow;
        const names = row.operator_names || [];
        if (!names || names.length === 0) {
          return <span className="text-muted">Unassigned</span>;
        }
        const firstName = names[0]?.split(' ')[0] || 'Unknown';
        if (names.length === 1) {
          return <span title={names[0]}>{firstName}</span>;
        }
        return (
          <span title={names.join(', ')}>
            {firstName} <span className="text-muted">+{names.length - 1}</span>
          </span>
        );
      }
    }
  ], [formatBookingDate]);

  const filters = useMemo(() => [
    { key: 'status', label: 'Status', options: statusOptions },
    { key: 'assigned_to', label: 'Operator', options: operatorOptions, matchFn: (r: Record<string, unknown>, value: string) => { const row = r as BookingTableRow; return Array.isArray(row.assigned_to) && row.assigned_to.includes(value); } },
    { key: 'territory_id', label: 'Territory', options: territoryOptions },
    { key: 'is_pos', label: 'Type', options: contextOptions, matchFn: (r: Record<string, unknown>, value: string) => { const row = r as BookingTableRow; return value === 'pos' ? row.is_pos === true : row.is_pos !== true; } },
    { key: 'source', label: 'Source', options: sourceOptions }
  ], [statusOptions, operatorOptions, territoryOptions, contextOptions, sourceOptions]);

  return { columns, filters };
}
